// 登录用户状态：管理会话、学校班级上下文、题库令牌与 Office 形象选择。
import { login as loginByUsersApi } from "@/api/users";
import {
  getUserInfo as getUserInfoByUsersApi,
  logout as logoutByUsersApi,
} from "@/api/users";
import type { ClassListItem, UserInfo } from "@/api/user";
import { resetRouter } from "@/router";
import { routePermission } from "@/permission";
import { store } from "@/store";
import { QB_TOKEN_KEY, TOKEN_KEY } from "@/enums/CacheEnum";
import { getQuestionBankToken } from "@/api/questionBank";
import { useStorage } from "@vueuse/core";
import md5 from "md5";
import type { RequestErrorHandler } from "@/types/request";
import boyAvatar1 from "@/assets/images/boy1_bg.png";
import boyAvatar2 from "@/assets/images/boy2_bg.png";
import boyAvatar3 from "@/assets/images/boy3_bg.png";
import girlAvatar1 from "@/assets/images/girl1_bg.png";
import girlAvatar2 from "@/assets/images/girl2_bg.png";
import girlAvatar3 from "@/assets/images/girl3_bg.png";

// 头像序号固定为三档，与 itestaiclassroom 的本地存储值保持兼容。
type AvatarIndex = "1" | "2" | "3";

// 男性用户可选择的三套形象。
const boyAvatarImages = [boyAvatar1, boyAvatar2, boyAvatar3] as const;
// 女性用户可选择的三套形象。
const girlAvatarImages = [girlAvatar1, girlAvatar2, girlAvatar3] as const;
// 后端不同部署可能使用中文、英文或 0 表示女性。
const femaleSexValues = new Set(["女", "0", "woman", "female"]);

interface LoginData {
  username: string;
  password: string;
  captchaKey?: string;
  captchaCode?: string;
}

interface UsersApiLoginResult {
  token?: string;
  tokenType?: string;
  accessToken?: string;
}

interface QuestionBankTokenResponse {
  // 少数部署会直接返回令牌
  accessToken?: string;
  // 标准响应通过 data 包裹业务结果，兼容历史双层 data 结构
  data?: {
    // 标准题库令牌字段
    accessToken?: string;
    // 历史代理可能再次包裹 data
    data?: {
      // 双层 data 响应中的题库令牌
      accessToken?: string;
    };
  };
}

export const useUserStore = defineStore("user", () => {
  const user = ref<UserInfo>({
    roles: [],
    perms: [],
  });
  /** 当前选中的学校（与 itestaiclassroom 一致，持久化；空则回退 user.schoolIds[0]） */
  const schoolId = useStorage<string>(
    "schoolId",
    (user.value.schoolIds?.[0] ?? "") as string
  );
  /** 班级列表（由 getUserInfo 的 classNames 填充，持久化） */
  const classList = useStorage<ClassListItem[]>("classList", []);
  // 用户选择的形象序号独立持久化，默认使用每组的第一套形象。
  const userAvatar = useStorage<AvatarIndex>("userAvatar", "1");
  // 题库访问令牌，同时与 localStorage 保持一致，供跟读页面和请求拦截器共用
  const questionBankToken = ref<string>(
    localStorage.getItem(QB_TOKEN_KEY) || ""
  );
  // 作文批改服务的短期授权 ID，刷新页面后仍可继续当前上传任务。
  const writingCorrectWarrantId = useStorage<string>(
    "writingCorrectWarrantId",
    ""
  );

  /** 登出 / token 失效时清理与用户绑定的持久化数据 */
  function clearUserSessionData() {
    schoolId.value = "";
    classList.value = [];
    writingCorrectWarrantId.value = "";
    user.value = { roles: [], perms: [] };
  }

  /**
   * 保存作文批改服务返回的短期授权 ID。
   * @param value 鉴权接口返回的 warrant_id
   */
  function setWritingCorrectWarrantId(value: string): void {
    writingCorrectWarrantId.value = value;
  }

  /** 将持久化 schoolId 与当前用户信息对齐（过期或不在列表中则回退） */
  function syncSchoolId(userInfo: UserInfo) {
    const schoolIds = userInfo.schoolIds ?? [];
    const fallback = (schoolIds[0] || userInfo.schoolId || "") as string;

    if (!schoolId.value) {
      schoolId.value = fallback;
      return;
    }

    if (schoolIds.length > 0 && !schoolIds.includes(schoolId.value)) {
      schoolId.value = fallback;
    }
  }

  /**
   * 将外部头像序号收敛到三套合法值，避免旧缓存或异常参数导致图片为空。
   * @param value 待校验的头像序号
   * @returns 可安全使用的头像序号
   */
  function normalizeAvatarIndex(value: string | number): AvatarIndex {
    const normalizedValue = String(value);
    return normalizedValue === "2" || normalizedValue === "3"
      ? normalizedValue
      : "1";
  }

  /**
   * 判断当前用户是否属于女性头像组。
   * @returns 是否使用女性形象素材
   */
  function isFemaleUser(): boolean {
    return femaleSexValues.has(String(user.value.sex ?? "").toLowerCase());
  }

  /**
   * 获取当前性别对应的三套候选形象。
   * @returns 当前用户可选的三张头像图片
   */
  function getSexAvatarOptions(): readonly string[] {
    return isFemaleUser() ? girlAvatarImages : boyAvatarImages;
  }

  /**
   * 按当前用户性别和头像序号解析最终显示图片。
   * @param index 指定头像序号；不传时使用已持久化的选择
   * @returns 对应的头像资源地址
   */
  function getSexImage(index: string | number = userAvatar.value): string {
    const avatarIndex = Number(normalizeAvatarIndex(index)) - 1;
    return getSexAvatarOptions()[avatarIndex];
  }

  /**
   * 保存用户选择的头像序号。
   * @param value 用户选中的头像序号
   */
  function changeUserAvatar(value: string | number): void {
    userAvatar.value = normalizeAvatarIndex(value);
  }

  /**
   * 登录
   *
   * @param {LoginData}
   * @returns
   */
  function login(loginData: LoginData, onError?: RequestErrorHandler) {
    return new Promise<void>((resolve, reject) => {
      const payload = {
        ...loginData,
        username: loginData.username.trim(),
        password: loginData.password.trim(),
      };

      loginByUsersApi(payload, onError)
        .then(async (data: any) => {
          const loginResult: UsersApiLoginResult = data?.data || data || {};
          const token =
            loginResult.token ||
            (loginResult.tokenType && loginResult.accessToken
              ? `${loginResult.tokenType} ${loginResult.accessToken}`
              : "");
          if (!token) {
            reject(new Error("登录成功但未返回 token"));
            return;
          }
          localStorage.setItem(TOKEN_KEY, token);
          try {
            await tryFetchQuestionBankToken();
          } catch (error) {
            // 题库 token 获取失败不阻断主登录流程
            console.warn("Failed to fetch question bank token:", error);
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // 获取信息(用户昵称、头像、角色集合、权限集合)
  function getUserInfo() {
    return new Promise<UserInfo>((resolve, reject) => {
      getUserInfoByUsersApi()
        .then((res: any) => {
          const data = (res?.data || res || {}) as UserInfo;
          if (!Object.keys(data).length) {
            reject("Verification failed, please Login again.");
            return;
          }
          if (!data.roles || data.roles.length <= 0) {
            reject("getUserInfo: roles must be a non-null array!");
            return;
          }
          const userInfo = { ...data, perms: data.perms || [] } as UserInfo;
          Object.assign(user.value, userInfo);

          if (userInfo.classNames?.length) {
            classList.value = userInfo.classNames.map((item) => ({
              ...item,
              id: md5(
                `${item.gradeNo ?? ""}-${encodeURIComponent(item.className)}`
              ),
            }));
          }
          syncSchoolId(userInfo);

          resolve(userInfo);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // user logout
  function logout() {
    return new Promise<void>((resolve, reject) => {
      // 跳过登出接口调用，直接执行清理逻辑
      localStorage.setItem(TOKEN_KEY, "");
      localStorage.setItem(QB_TOKEN_KEY, "");
      questionBankToken.value = "";
      clearUserSessionData();
      routePermission.reset();
      location.reload(); // 清空路由
      resolve();
    });
  }

  // remove token
  function resetToken() {
    return new Promise<void>((resolve) => {
      localStorage.setItem(TOKEN_KEY, "");
      localStorage.setItem(QB_TOKEN_KEY, "");
      questionBankToken.value = "";
      clearUserSessionData();
      routePermission.reset();
      resetRouter();
      resolve();
    });
  }

  /**
   * 登录后按部署环境预取题库令牌；失败不会阻断主登录流程
   * @returns 获取到的令牌；当前环境无需预取时返回空字符串
   */
  async function tryFetchQuestionBankToken(): Promise<string> {
    const shouldFetchQBankToken =
      window.location.href.includes("https://b.szshipian.com") ||
      window.location.href.includes("http://192.168.0.2") ||
      import.meta.env.DEV;
    if (!shouldFetchQBankToken) {
      return "";
    }

    return fetchQuestionBankToken();
  }

  /**
   * 获取并缓存单词跟读所需的题库访问令牌
   * @returns 可供题库接口直接使用的访问令牌
   */
  async function fetchQuestionBankToken(): Promise<string> {
    const appId = import.meta.env.VITE_APP_QBANK_ID;
    const appSecret = import.meta.env.VITE_APP_QBANK_SECRET;
    if (!appId || !appSecret) {
      throw new Error(
        "VITE_APP_QBANK_ID or VITE_APP_QBANK_SECRET is not defined"
      );
    }

    const response = (await getQuestionBankToken({
      appId,
      appSecret,
      expiresIn: 600000,
    })) as unknown as QuestionBankTokenResponse;
    const accessToken =
      response?.data?.accessToken ||
      response?.data?.data?.accessToken ||
      response?.accessToken ||
      "";
    if (!accessToken) {
      throw new Error("题库令牌响应中缺少 accessToken");
    }

    localStorage.setItem(QB_TOKEN_KEY, accessToken);
    questionBankToken.value = accessToken;
    return accessToken;
  }

  return {
    user,
    /** 与前台组件命名对齐，等价于 `user` */
    userInfo: user,
    schoolId,
    classList,
    userAvatar,
    questionBankToken,
    writingCorrectWarrantId,
    getSexAvatarOptions,
    getSexImage,
    changeUserAvatar,
    setWritingCorrectWarrantId,
    fetchQuestionBankToken,
    login,
    getUserInfo,
    logout,
    resetToken,
  };
});

/**
 * 用于在组件外部（如在Pinia Store 中）使用 Pinia 提供的 store 实例。
 * 官方文档解释了如何在组件外部使用 Pinia Store：
 * https://pinia.vuejs.org/core-concepts/outside-component-usage.html#using-a-store-outside-of-a-component
 */
export function useUserStoreHook() {
  return useUserStore(store);
}
